import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { 
  Users, Mail, Phone, Calendar, Download, Search, Filter, 
  TrendingUp, Eye, MoreHorizontal, UserPlus, Gift, ExternalLink,
  BarChart3, PieChart, Activity
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LeadManagement = () => {
  const { user, token: authToken } = useAuth();
  
  // State management
  const [leads, setLeads] = useState([]);
  const [pages, setPages] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('30d');
  const [currentTab, setCurrentTab] = useState('leads');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchLeads();
    fetchPages();
    fetchAnalytics();
  }, [currentPage, selectedPage, searchQuery, dateRange]);

  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams({
        page: currentPage,
        page_size: 20,
        search: searchQuery,
        date_range: dateRange
      });

      if (selectedPage !== 'all') {
        params.append('page_id', selectedPage);
      }

      const response = await fetch(
        `${BACKEND_URL}/api/member/lead-capture/leads?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      const data = await response.json();
      
      if (data.ok) {
        setLeads(data.leads);
        setTotalPages(Math.ceil(data.total_count / 20));
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error("Failed to load leads. Please try again.");
    }
  };

  const fetchPages = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/member/lead-capture/customizations`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      const data = await response.json();
      
      if (data.ok) {
        setPages(data.customizations || []);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/member/lead-capture/analytics?date_range=${dateRange}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      const data = await response.json();
      
      if (data.ok) {
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvitePIF = async (lead) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/member/lead-capture/invite-pif/${lead.id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      const data = await response.json();
      
      if (data.ok) {
        toast.success(`PIF Invitation Sent! ${lead.name} has been invited to join with PIF voucher ${data.voucher_code}`);
        
        // Update lead status
        setLeads(prev => prev.map(l => 
          l.id === lead.id 
            ? { ...l, pif_invited: true, pif_voucher_code: data.voucher_code }
            : l
        ));
      }
    } catch (error) {
      console.error('Error inviting to PIF:', error);
      toast.error("Failed to send PIF invitation. Please try again.");
    }
  };

  const handleExportLeads = async () => {
    try {
      const params = new URLSearchParams({
        search: searchQuery,
        date_range: dateRange,
        format: 'csv'
      });

      if (selectedPage !== 'all') {
        params.append('page_id', selectedPage);
      }

      const response = await fetch(
        `${BACKEND_URL}/api/member/lead-capture/leads/export?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        toast.success("Export Complete - Your leads have been downloaded as a CSV file.");
      }
    } catch (error) {
      console.error('Error exporting leads:', error);
      toast.error("Failed to export leads. Please try again.");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (lead) => {
    if (lead.pif_converted) {
      return <Badge className="bg-green-100 text-green-800">Converted</Badge>;
    } else if (lead.pif_invited) {
      return <Badge className="bg-blue-100 text-blue-800">PIF Invited</Badge>;
    }
    return <Badge variant="outline">New Lead</Badge>;
  };

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Lead Management</h1>
          <p className="text-gray-600">Manage your captured leads and track conversions</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={handleExportLeads}>
            <Download className="h-4 w-4 mr-2" />
            Export Leads
          </Button>
          <Button onClick={() => window.location.href = '/member/lead-capture/marketplace'}>
            <UserPlus className="h-4 w-4 mr-2" />
            Create New Page
          </Button>
        </div>
      </div>

      {/* Analytics Cards */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center space-x-2">
                <Users className="h-5 w-5 text-blue-500" />
                <span className="text-2xl font-bold text-gray-900">{analytics.total_leads || 0}</span>
              </div>
              <p className="text-sm text-gray-600 mt-1">Total Leads</p>
              <p className="text-xs text-green-600">+{analytics.leads_today || 0} today</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center space-x-2">
                <Eye className="h-5 w-5 text-green-500" />
                <span className="text-2xl font-bold text-gray-900">{analytics.total_views || 0}</span>
              </div>
              <p className="text-sm text-gray-600 mt-1">Page Views</p>
              <p className="text-xs text-green-600">+{analytics.views_today || 0} today</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center space-x-2">
                <TrendingUp className="h-5 w-5 text-purple-500" />
                <span className="text-2xl font-bold text-gray-900">
                  {analytics.conversion_rate ? `${(analytics.conversion_rate * 100).toFixed(1)}%` : '0%'}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">Conversion Rate</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center space-x-2">
                <Gift className="h-5 w-5 text-yellow-500" />
                <span className="text-2xl font-bold text-gray-900">{analytics.pif_conversions || 0}</span>
              </div>
              <p className="text-sm text-gray-600 mt-1">PIF Conversions</p>
              <p className="text-xs text-blue-600">{analytics.pif_invites || 0} invites sent</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={currentTab} onValueChange={setCurrentTab}>
        <TabsList>
          <TabsTrigger value="leads" className="flex items-center space-x-2">
            <Users className="h-4 w-4" />
            <span>Leads ({leads.length})</span>
          </TabsTrigger>
          <TabsTrigger value="pages" className="flex items-center space-x-2">
            <ExternalLink className="h-4 w-4" />
            <span>My Pages ({pages.length})</span>
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center space-x-2">
            <BarChart3 className="h-4 w-4" />
            <span>Analytics</span>
          </TabsTrigger>
        </TabsList>

        {/* Leads Tab */}
        <TabsContent value="leads">
          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search leads..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <Select value={selectedPage} onValueChange={setSelectedPage}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Pages" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Pages</SelectItem>
                    {pages.map((page) => (
                      <SelectItem key={page.id} value={page.id}>
                        {page.page_title || `Page ${page.id.substring(0, 8)}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={dateRange} onValueChange={setDateRange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Date Range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7d">Last 7 days</SelectItem>
                    <SelectItem value="30d">Last 30 days</SelectItem>
                    <SelectItem value="90d">Last 90 days</SelectItem>
                    <SelectItem value="all">All time</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button variant="outline" onClick={fetchLeads}>
                  <Filter className="h-4 w-4 mr-2" />
                  Apply Filters
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Leads Table */}
          <Card>
            <CardHeader>
              <CardTitle>Captured Leads</CardTitle>
              <CardDescription>
                All leads captured from your lead generation pages
              </CardDescription>
            </CardHeader>
            <CardContent>
              {leads.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No leads yet</h3>
                  <p className="text-gray-600 mb-4">Create your first lead capture page to start collecting leads</p>
                  <Button onClick={() => window.location.href = '/member/lead-capture/marketplace'}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create Lead Page
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3">Name</th>
                        <th className="text-left p-3">Email</th>
                        <th className="text-left p-3">Phone</th>
                        <th className="text-left p-3">Source Page</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-left p-3">Date</th>
                        <th className="text-right p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((lead) => (
                        <tr key={lead.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <div>
                              <div className="font-medium">{lead.name}</div>
                              {lead.company && <div className="text-sm text-gray-500">{lead.company}</div>}
                            </div>
                          </td>
                          <td className="p-3">
                            <a href={`mailto:${lead.email}`} className="text-blue-600 hover:underline">
                              {lead.email}
                            </a>
                          </td>
                          <td className="p-3">
                            {lead.phone ? (
                              <a href={`tel:${lead.phone}`} className="text-blue-600 hover:underline">
                                {lead.phone}
                              </a>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="text-sm">
                              {lead.source_page || 'Unknown'}
                            </div>
                          </td>
                          <td className="p-3">
                            {getStatusBadge(lead)}
                          </td>
                          <td className="p-3 text-sm text-gray-600">
                            {formatDate(lead.created_at)}
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              {!lead.pif_invited && !lead.pif_converted && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleInvitePIF(lead)}
                                >
                                  <Gift className="h-4 w-4 mr-1" />
                                  Invite PIF
                                </Button>
                              )}
                              <Button size="sm" variant="outline">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-6 space-x-2">
              <Button 
                variant="outline" 
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </Button>
              
              {[...Array(totalPages)].map((_, index) => (
                <Button
                  key={index}
                  variant={currentPage === index + 1 ? "default" : "outline"}
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </Button>
              ))}
              
              <Button 
                variant="outline" 
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Pages Tab */}
        <TabsContent value="pages">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pages.map((page) => (
              <Card key={page.id} className="group hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="truncate">{page.page_title || `Page ${page.id.substring(0, 8)}`}</span>
                    <Badge variant="outline">
                      {page.status === 'published' ? 'Live' : 'Draft'}
                    </Badge>
                  </CardTitle>
                  <CardDescription className="truncate">
                    {page.template_title} - {page.industry_name}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">{page.leads_count || 0}</div>
                      <div className="text-xs text-gray-500">Leads</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">{page.views_count || 0}</div>
                      <div className="text-xs text-gray-500">Views</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">
                        {page.conversion_rate ? `${(page.conversion_rate * 100).toFixed(1)}%` : '0%'}
                      </div>
                      <div className="text-xs text-gray-500">CVR</div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => window.location.href = `/member/lead-capture/generate?template=${page.template_id}&customization=${page.id}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    {page.public_url && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => window.open(page.public_url, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {/* Create New Page Card */}
            <Card className="group hover:shadow-lg transition-shadow cursor-pointer border-dashed">
              <CardContent 
                className="flex flex-col items-center justify-center h-full p-8 text-center"
                onClick={() => window.location.href = '/member/lead-capture/marketplace'}
              >
                <UserPlus className="h-12 w-12 text-gray-400 group-hover:text-blue-500 transition-colors mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Create New Page</h3>
                <p className="text-gray-600 text-sm">Choose from our professional templates</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Performance Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-gray-500">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4" />
                  <p>Detailed analytics coming soon!</p>
                  <p className="text-sm">Charts, graphs, and insights</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Conversion Funnel</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-gray-500">
                  <PieChart className="h-12 w-12 mx-auto mb-4" />
                  <p>Conversion tracking coming soon!</p>
                  <p className="text-sm">Track leads through your funnel</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LeadManagement;